import { Model } from "@/stores/AbstractBase.store.ts";
import { Classroom } from "@/models/Classroom.model.ts";
import { Curriculum } from "@/models/Curriculum.model.ts";
import { useClassroomsStore } from "@/stores/Classrooms.store.ts";

export class GoalCollection implements Model {

	public id: string = '';
	public name: string = '';
	public goalCount: number = 0;
	public archivedAt: Date | null = null;

	public classroomIds: string[] = [];

	// One collection = one curriculum. Client-sent at creation, immutable server-side
	// (the API rejects changes). Null on legacy collections (pre-backfill / org without
	// curriculum) — consumers fall back to the school's curriculum.
	public curriculumId: string | null = null;

	static createFromApiData(data: any): GoalCollection
	{
		const goalCollection = new GoalCollection();
		goalCollection.hydrateWithApiData(data);

		return goalCollection;
	}

	hydrateWithApiData(data: any): void {
		this.id = data.id;
		this.name = data.name;
		this.goalCount = data.goalCount;
		this.archivedAt = data.archivedAt ? new Date(data.archivedAt) : null;

		this.classroomIds = data.classrooms ? data.classrooms.map((c: any) => c.id) : [];
		this.curriculumId = data.curriculum ?? null;
	}

	asApiDataObject(): any {
		return {
			id: this.id,
			name: this.name,
			classrooms: this.classroomIds.map(id => ({ id })),
			goalCount: this.goalCount,
			archivedAt: this.archivedAt,
			curriculum: this.curriculumId,
		};
	}

	toggleClassroom(classroom: Classroom) {
		if (this.classroomIds.includes(classroom.getId())) {
			this.unselectClassroom(classroom);
		} else {
			this.selectClassroom(classroom);
		}
	}

	unselectClassroom(classroom: Classroom) {
		this.classroomIds = this.classroomIds.filter(id => id !== classroom.id);
	}

	selectClassroom(classroom: Classroom) {
		if (!this.classroomIds.includes(classroom.getId())) {
			this.classroomIds.push(classroom.getId());
		}
	}

	isClassroomSelected(classroom: Classroom): boolean {
		return this.classroomIds.includes(classroom.getId());
	}

	get classrooms(): Classroom[] {
		return useClassroomsStore().findAll(this.classroomIds);
	}

	get curriculum(): Curriculum | null {
		return this.curriculumId ? new Curriculum(this.curriculumId) : null;
	}

	clone() {
		const clone = new GoalCollection();
		clone.id = this.id;
		clone.name = this.name;
		clone.classroomIds = [ ...this.classroomIds ];
		clone.goalCount = this.goalCount;
		clone.archivedAt = this.archivedAt;
		clone.curriculumId = this.curriculumId;
		return clone;
	}

	equals(other: Model): boolean {
		if (!(other instanceof GoalCollection)) {
			return false;
		}

		return JSON.stringify(this.asApiDataObject()) === JSON.stringify(other.asApiDataObject());
	}

	get editable(): boolean {
		return this.archivedAt === null;
	}

}
